import {
  LEARNING_PATH_KIND,
  LEARNING_PATH_VERSION,
  type LearningPathBundle,
  type LearningPathSpaceDto,
  type NestedUnitDto,
} from "./types";

export class LearningPathParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LearningPathParseError";
  }
}

function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new LearningPathParseError(`${label} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function asUnitArray(value: unknown): NestedUnitDto[] {
  if (!Array.isArray(value)) {
    throw new LearningPathParseError('"units" must be a non-empty array.');
  }
  if (value.length === 0) {
    throw new LearningPathParseError('"units" must be a non-empty array.');
  }
  return value.map((item, index) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      throw new LearningPathParseError(
        `units[${index}] must be an object.`
      );
    }
    return item as NestedUnitDto;
  });
}

export function parseLearningPathJson(parsed: unknown): LearningPathBundle {
  const record = asRecord(parsed, "JSON");

  if (record.kind !== LEARNING_PATH_KIND) {
    throw new LearningPathParseError(
      `Unsupported kind: ${String(record.kind)}. Expected "${LEARNING_PATH_KIND}".`
    );
  }

  if (record.version !== LEARNING_PATH_VERSION) {
    throw new LearningPathParseError(
      `Unsupported version: ${String(record.version)}. Expected ${LEARNING_PATH_VERSION}.`
    );
  }

  const space = asRecord(record.space, '"space"') as LearningPathSpaceDto;
  const units = asUnitArray(record.units);

  return {
    version: LEARNING_PATH_VERSION,
    kind: LEARNING_PATH_KIND,
    space,
    units,
  };
}

export function parseLearningPathBundle(text: string): LearningPathBundle {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new LearningPathParseError("Invalid JSON.");
  }
  return parseLearningPathJson(parsed);
}
